import React, { useState } from 'react';

import Button from '../../../components/ui/Button';

const CalendarView = ({ events, selectedDate, onDateChange, registrations }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const getDaysInMonth = (date) => {
    const year = date?.getFullYear();
    const month = date?.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay?.getDate();
    const startingDayOfWeek = firstDay?.getDay();

    const days = [];
    for (let i = 0; i < startingDayOfWeek; i++) {
      days?.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days?.push(new Date(year, month, i));
    }
    return days;
  };

  const getEventsForDate = (date) => {
    if (!date) return [];
    return events?.filter(event => {
      const eventDate = new Date(event?.start_time);
      return eventDate?.toDateString() === date?.toDateString();
    });
  };

  const isRegisteredForDate = (date) => {
    const dateEvents = getEventsForDate(date);
    return dateEvents?.some(event => 
      registrations?.some(r => r?.event_id === event?.id)
    );
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentMonth);
    newDate?.setMonth(currentMonth?.getMonth() + direction);
    setCurrentMonth(newDate);
  };

  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return date?.toDateString() === today?.toDateString();
  };

  const isSelected = (date) => {
    if (!date) return false;
    return date?.toDateString() === selectedDate?.toDateString();
  };

  const days = getDaysInMonth(currentMonth);
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const monthYear = currentMonth?.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  return (
    <div className="bg-card border border-border rounded-lg shadow-md p-4 md:p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-heading font-semibold text-foreground">
          {monthYear}
        </h2>
        
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigateMonth(-1)}
            iconName="ChevronLeft"
            iconSize={16}
            className="w-10 h-10 p-0"
            aria-label="Previous month"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setCurrentMonth(new Date());
              onDateChange(new Date());
            }}
            className="hidden md:flex"
          >
            Today
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigateMonth(1)}
            iconName="ChevronRight"
            iconSize={16}
            className="w-10 h-10 p-0"
            aria-label="Next month"
          />
        </div>
      </div>

      <div className="grid grid-cols-7 gap-2 mb-2">
        {weekDays?.map(day => (
          <div key={day} className="text-center text-xs font-semibold text-muted-foreground py-2">
            {day}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-2">
        {days?.map((date, index) => {
          const dayEvents = date ? getEventsForDate(date) : [];
          const hasEvents = dayEvents?.length > 0;
          const isReg = date ? isRegisteredForDate(date) : false;
          const today = date ? isToday(date) : false;
          const selected = date ? isSelected(date) : false;

          return (
            <button
              key={index}
              onClick={() => date && onDateChange(date)}
              disabled={!date}
              className={`
                relative aspect-square p-2 rounded-lg text-sm font-medium
                transition-smooth hover-lift
                ${!date ? 'invisible' : ''}
                ${selected 
                  ? 'bg-primary text-primary-foreground shadow-md' 
                  : today
                  ? 'bg-accent/10 text-accent border-2 border-accent' :'bg-muted text-foreground hover:bg-muted/80'
                }
                ${!date ? 'cursor-default' : 'cursor-pointer'}
              `}
            >
              {date && (
                <>
                  <span className="block">{date?.getDate()}</span>
                  {hasEvents && (
                    <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2 flex gap-1">
                      <div className={`w-1 h-1 rounded-full ${selected ? 'bg-primary-foreground' : isReg ? 'bg-success' : 'bg-primary'}`} />
                      {dayEvents?.length > 1 && (
                        <div className={`w-1 h-1 rounded-full ${selected ? 'bg-primary-foreground' : 'bg-primary'}`} />
                      )}
                    </div>
                  )}
                </>
              )}
            </button>
          );
        })}
      </div>

      <div className="mt-4 flex items-center gap-4 text-xs text-muted-foreground">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-primary" />
          <span>Has Events</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-success" />
          <span>Registered</span>
        </div>
      </div>
    </div>
  );
};

export default CalendarView;
